import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { 
  ShoppingBag,
  Wrench,
  Briefcase,
  Users,
  HardHat,
  Building2,
  ArrowRight,
  CheckCircle2,
  Menu,
  X,
  Search,
  LayoutDashboard,
  Globe,
  Play,
  Package,
  TrendingUp,
  Shield,
  Truck,
  Star,
  ChevronRight,
  Clock,
  Bell,
  Sparkles,
  PlayCircle,
  FileText,
  UserPlus,
  Phone,
  Mail,
  MapPin,
  Home,
  GraduationCap,
  Camera
} from "lucide-react";
import InfiniteImageCarousel from "@/components/InfiniteImageCarousel";
import useScrollTracking from "@/hooks/useScrollTracking";

const API = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for dynamic icons from CMS
const ICON_MAP = {
  Building2, Users, HardHat, Briefcase, ShoppingBag, Wrench, Star, Shield,
  TrendingUp, Truck, FileText, Clock, CheckCircle2, Package, Phone, Mail, MapPin,
  Play, UserPlus, Bell, Sparkles, PlayCircle, ArrowRight, Search, Globe
};

// Color mapping for dynamic colors from CMS
const COLOR_MAP = {
  blue: { bg: "from-blue-500/20 to-blue-500/5", border: "border-blue-500/30", text: "text-blue-400", iconBg: "bg-blue-500/20" },
  green: { bg: "from-green-500/20 to-green-500/5", border: "border-green-500/30", text: "text-green-400", iconBg: "bg-green-500/20" },
  purple: { bg: "from-purple-500/20 to-purple-500/5", border: "border-purple-500/30", text: "text-purple-400", iconBg: "bg-purple-500/20" },
  orange: { bg: "from-orange-500/20 to-orange-500/5", border: "border-orange-500/30", text: "text-orange-400", iconBg: "bg-orange-500/20" },
  cyan: { bg: "from-[#23F7DD]/20 to-[#23F7DD]/5", border: "border-[#23F7DD]/30", text: "text-[#23F7DD]", iconBg: "bg-[#23F7DD]/20" },
  pink: { bg: "from-pink-500/20 to-pink-500/5", border: "border-pink-500/30", text: "text-pink-400", iconBg: "bg-pink-500/20" },
  yellow: { bg: "from-yellow-500/20 to-yellow-500/5", border: "border-yellow-500/30", text: "text-yellow-400", iconBg: "bg-yellow-500/20" },
  red: { bg: "from-red-500/20 to-red-500/5", border: "border-red-500/30", text: "text-red-400", iconBg: "bg-red-500/20" }
};

// Default translations (fallback if CMS is empty)
const defaultTranslations = {
  en: {
    tagline: "Professional Scaffolding Services",
    subtitle: "Complete scaffolding solutions delivered by AllStillas Holding — Norway's trusted scaffold contractor",
    shop: "Shop Parts",
    rent: "Rent Equipment",
    projects: "Our Services",
    workers: "Careers",
    getStarted: "Register",
    logIn: "Log In",
    dashboard: "Dashboard",
    stats: { projects: "Projects Delivered", workers: "Team Members", products: "Products", companies: "Clients Served" },
    howItWorks: "How It Works",
    howItWorksSubtitle: "Simple process from request to project completion",
    workflowSteps: [
      { title: "Submit Request", desc: "Describe your scaffolding needs through our digital platform" },
      { title: "Receive Quote", desc: "AllStillas Holding reviews and provides a professional quote" },
      { title: "Sign Contract", desc: "One contract between you and AllStillas Holding" },
      { title: "Project Delivery", desc: "Our employed teams deliver your complete project" }
    ],
    cta: "Ready to start your project?",
    ctaDesc: "Get a quote from Norway's trusted scaffolding contractor",
    comingSoon: "Coming Soon",
    emailPlaceholder: "Enter your email",
    notifyMe: "Get Updates",
    thankYou: "Thank you! We'll be in touch soon."
  },
  no: {
    tagline: "Profesjonelle Stillastjenester",
    subtitle: "Komplette stillasløsninger levert av AllStillas Holding — Norges pålitelige stillasentreprenør",
    shop: "Kjøp Deler",
    rent: "Lei Utstyr",
    projects: "Våre Tjenester",
    workers: "Karriere",
    getStarted: "Registrer",
    logIn: "Logg Inn",
    dashboard: "Dashboard",
    stats: { projects: "Leverte Prosjekter", workers: "Teammedlemmer", products: "Produkter", companies: "Betjente Kunder" },
    howItWorks: "Slik Fungerer Det",
    howItWorksSubtitle: "Enkel prosess fra forespørsel til prosjektlevering",
    workflowSteps: [
      { title: "Send Forespørsel", desc: "Beskriv dine stillasbehov via vår digitale plattform" },
      { title: "Motta Tilbud", desc: "AllStillas Holding vurderer og gir et profesjonelt tilbud" },
      { title: "Signer Kontrakt", desc: "Én kontrakt mellom deg og AllStillas Holding" },
      { title: "Prosjektlevering", desc: "Våre ansatte team leverer ditt komplette prosjekt" }
    ],
    cta: "Klar til å starte prosjektet?",
    ctaDesc: "Få tilbud fra Norges pålitelige stillasentreprenør",
    comingSoon: "Kommer Snart",
    emailPlaceholder: "Skriv inn e-posten din",
    notifyMe: "Få Oppdateringer",
    thankYou: "Takk! Vi tar kontakt snart."
  }
};

export default function LandingPage() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyType, setNotifyType] = useState("");
  const [notifySuccess, setNotifySuccess] = useState(false);
  const [stats, setStats] = useState({ projects: 34, workers: 20, products: 0, companies: 20 });

  // Force the browser tab / PWA prompt back to AllStillas whenever the
  // user lands on (or navigates back to) the marketplace root. React
  // Helmet on other routes may have flipped it to "Doserne Academy",
  // so we explicitly reset here — Doserne stays Doserne on its own
  // /doserne routes.
  useEffect(() => {
    document.title = "AllStillas — Scaffolding Platform";
    const appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (appleTitle) appleTitle.setAttribute("content", "AllStillas");
    const appName = document.querySelector('meta[name="application-name"]');
    if (appName) appName.setAttribute("content", "AllStillas");
  }, []);
  
  // Scroll tracking for analytics
  useScrollTracking("home");
  
  // CMS State
  const [cmsConfig, setCmsConfig] = useState(null);
  const [cmsLoading, setCmsLoading] = useState(true);
  
  // Project images for gallery
  const [projectImages, setProjectImages] = useState([]);

  // Language state - read from localStorage or i18n
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem("allstillas_lang");
    if (saved && (saved === "en" || saved === "no")) return saved;
    return i18n.language === "no" ? "no" : "en";
  });

  const t = defaultTranslations[lang];

  // Fetch CMS config
  const fetchCmsConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/landing-cms/config`);
      setCmsConfig(res.data);
    } catch (err) {
    } finally {
      setCmsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCmsConfig();
    
    // Fetch project images for home gallery
    const fetchProjectImages = async () => {
      try {
        const res = await axios.get(`${API}/api/banner/project-images/home`);
        setProjectImages(res.data.images || []);
      } catch (err) {
        console.debug("Project images fetch failed:", err);
      }
    };
    fetchProjectImages();
  }, [fetchCmsConfig]);

  // Sync language on mount and when i18n changes
  useEffect(() => {
    const saved = localStorage.getItem("allstillas_lang");
    if (saved && (saved === "en" || saved === "no")) {
      setLang(saved);
      if (i18n.language !== saved) {
        i18n.changeLanguage(saved);
      }
    }
  }, [i18n]);

  const toggleLang = () => {
    const newLang = lang === "en" ? "no" : "en";
    setLang(newLang);
    localStorage.setItem("allstillas_lang", newLang);
    i18n.changeLanguage(newLang);
  };

  const handleNotifySubmit = async (type) => {
    if (!notifyEmail || !notifyEmail.includes("@")) return;
    try {
      await axios.post(`${API}/api/notifications/coming-soon`, { email: notifyEmail, type, language: lang });
      setNotifySuccess(true);
      setNotifyEmail("");
      setTimeout(() => setNotifySuccess(false), 3000);
    } catch (err) {
      const stored = JSON.parse(localStorage.getItem("allstillas_notify") || "[]");
      stored.push({ email: notifyEmail, type, date: new Date().toISOString() });
      localStorage.setItem("allstillas_notify", JSON.stringify(stored));
      setNotifySuccess(true);
      setNotifyEmail("");
      setTimeout(() => setNotifySuccess(false), 3000);
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [projectsRes, workersRes, productsRes] = await Promise.all([
          axios.get(`${API}/api/projects/public`).catch(() => ({ data: [] })),
          axios.get(`${API}/api/workers/public-count`).catch(() => ({ data: { count: 0 } })),
          axios.get(`${API}/api/shop/products/public`).catch(() => ({ data: [] }))
        ]);
        setStats({
          projects: projectsRes.data?.length || 34,
          workers: Math.max(workersRes.data?.count || 0, 20),
          products: productsRes.data?.length || 0,
          companies: 20
        });
      } catch (err) {
        setStats({ projects: 34, workers: 20, products: 0, companies: 20 });
      }
    };
    fetchStats();
  }, []);

  // CMS Helpers
  const getHeroTitle = () => cmsConfig?.hero?.[`title_${lang}`] || t.tagline;
  const getHeroSubtitle = () => cmsConfig?.hero?.[`subtitle_${lang}`] || t.subtitle;
  const getHeroVideo = () => cmsConfig?.hero?.background_video || "/videos/xstillas-explainer.mp4";
  const getHeroImage = () => cmsConfig?.hero?.background_image || "https://images.pexels.com/photos/2219024/pexels-photo-2219024.jpeg?auto=compress&cs=tinysrgb&w=1920";
  const getPrimaryButton = () => cmsConfig?.hero?.primary_button || { text_en: "Post Project", text_no: "Publiser Prosjekt", link: "/register" };
  const getSecondaryButton = () => {
    const btn = cmsConfig?.hero?.secondary_button;
    if (btn && btn.link) return btn;
    return { text_en: "Learn More", text_no: "Les Mer", link: "/discover" };
  };
  
  const getCmsServices = () => cmsConfig?.services?.sort((a, b) => a.order - b.order) || [];
  const getCmsVideos = () => cmsConfig?.how_it_works?.videos?.sort((a, b) => a.order - b.order) || [];
  const getCmsStats = () => cmsConfig?.stats?.sort((a, b) => a.order - b.order) || [];
  const getCmsTestimonials = () => cmsConfig?.testimonials?.sort((a, b) => a.order - b.order) || [];
  const getCmsFooter = () => cmsConfig?.footer || {};

  const getDashboardLink = () => {
    if (!user) return "/login";
    switch (user.role) {
      case "webshop_admin": return "/admin/webshop";
      case "rentals_admin": return "/admin/rentals";
      case "platform_admin": case "operations_manager": return "/admin";
      case "team_leader": return "/team-leader";
      case "worker": return "/worker";
      case "holding_admin": return "/holding";
      default: return "/client";
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden">
      {/* Explicit AllStillas branding on this page — the inline
          script in index.html defaults to AllStillas, and the useEffect
          above resets tab title/PWA prompt when navigating back here
          from a /doserne route. Doserne branding only kicks in on
          /doserne/* URLs. */}
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover opacity-30"
          poster={getHeroImage()}
        >
          <source src={getHeroVideo()} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/80 via-[#050505]/60 to-[#050505]"></div>
      </div>

      {/* Navigation */}
      <nav className="relative z-10 flex items-center justify-between px-4 md:px-12 py-4 md:py-6 pt-[calc(env(safe-area-inset-top,0px)+1rem)] md:pt-6">
        <Link to="/" className="flex items-center gap-1">
          <div className="px-2 py-1 md:px-3 md:py-1.5 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded flex items-center justify-center">
            <span className="text-[#23F7DD] font-bold text-lg md:text-xl font-['Unbounded']">All</span>
          </div>
          <span className="text-xl md:text-2xl font-bold font-['Unbounded'] neon-text-subtle tracking-tight">Stillas</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={toggleLang} className="text-[#94A3B8] hover:text-white gap-2">
            <Globe className="w-4 h-4" />
            {lang === "en" ? "NO" : "EN"}
          </Button>
          <Link to="/webshop">
            <Button variant="ghost" className="text-white hover:text-[#23F7DD] hover:bg-white/5 gap-2">
              <ShoppingBag className="w-4 h-4" />{t.shop}
            </Button>
          </Link>
          <Link to="/rentals">
            <Button variant="ghost" className="text-white hover:text-[#23F7DD] hover:bg-white/5 gap-2">
              <Wrench className="w-4 h-4" />{t.rent}
            </Button>
          </Link>
          <a href="https://bruktstillas.com" target="_blank" rel="noopener noreferrer">
            <Button variant="ghost" className="text-white hover:text-[#F7931A] hover:bg-white/5 gap-2">
              <Package className="w-4 h-4" />{lang === "en" ? "Used Scaffolding" : "Brukt Stillas"}
            </Button>
          </a>
          <Link to="/hvordan-det-fungerer">
            <Button variant="ghost" className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 gap-2 font-semibold">
              <Play className="w-4 h-4" />{lang === "en" ? "How it works" : "Hvordan det fungerer"}
            </Button>
          </Link>
          <Link to="/projects">
            <Button variant="ghost" className="text-white hover:text-[#23F7DD] hover:bg-white/5 gap-2">
              <Briefcase className="w-4 h-4" />{t.projects}
            </Button>
          </Link>
          {user ? (
            <Link to={getDashboardLink()}>
              <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold gap-2">
                <LayoutDashboard className="w-4 h-4" />{t.dashboard}
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" className="text-white hover:text-[#23F7DD]">{t.logIn}</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold">{t.getStarted}</Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <div className="flex md:hidden items-center gap-2">
          {!user && (
            <>
              <Link to="/hvordan-det-fungerer">
                <Button size="sm" className="bg-orange-500 text-white hover:bg-orange-400 font-bold px-3 text-xs">
                  Info
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10 font-medium px-3">
                  {t.logIn}
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold px-3 shadow-lg shadow-[#23F7DD]/30">
                  {t.getStarted}
                </Button>
              </Link>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={toggleLang} className="text-[#94A3B8] hover:text-white">
            <Globe className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="relative z-20 md:hidden bg-[#0A0A0A]/95 backdrop-blur-lg border-b border-white/10">
          <div className="px-4 py-6 space-y-4">
            <Link to="/webshop" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-white hover:text-[#23F7DD] gap-3">
                <ShoppingBag className="w-5 h-5" />{t.shop}
              </Button>
            </Link>
            <Link to="/rentals" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-white hover:text-[#23F7DD] gap-3">
                <Wrench className="w-5 h-5" />{t.rent}
              </Button>
            </Link>
            <a href="https://bruktstillas.com" target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-white hover:text-[#F7931A] gap-3">
                <Package className="w-5 h-5" />{lang === "en" ? "Used Scaffolding" : "Brukt Stillas"}
              </Button>
            </a>
            <Link to="/hvordan-det-fungerer" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 gap-3 font-semibold">
                <Play className="w-5 h-5" />{lang === "en" ? "How it works" : "Hvordan det fungerer"}
              </Button>
            </Link>
            <Link to="/projects" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-white hover:text-[#23F7DD] gap-3">
                <Briefcase className="w-5 h-5" />{t.projects}
              </Button>
            </Link>
            <div className="pt-4 border-t border-white/10 space-y-3">
              {user ? (
                <Link to={getDashboardLink()} onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold gap-2">
                    <LayoutDashboard className="w-4 h-4" />{t.dashboard}
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full border-white/30 text-white">{t.logIn}</Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold">{t.getStarted}</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative z-10 px-4 md:px-12 pt-12 md:pt-20 pb-16">
        <div className="max-w-6xl mx-auto">
          {/* Services Title and Cards - At Top */}
          <div className="text-center mb-12">
            <Badge className="bg-[#23F7DD]/20 text-[#23F7DD] border-[#23F7DD]/30 px-4 py-1 text-sm mb-4">
              <Building2 className="w-4 h-4 inline mr-2" />
              {lang === "en" ? "Our Services" : "Våre Tjenester"}
            </Badge>
            <h2 className="text-3xl md:text-4xl font-['Unbounded'] font-bold text-white mb-4">
              {lang === "en" ? "Professional Scaffolding Solutions" : "Profesjonelle Stillasløsninger"}
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              {lang === "en" ? "Complete scaffolding services delivered by AllStillas Holding" : "Komplette stillastjenester levert av AllStillas Holding"}
            </p>
          </div>

          {/* Service Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {getCmsServices().length > 0 ? (
              getCmsServices().map((service) => {
                const colors = COLOR_MAP[service.color] || COLOR_MAP.blue;
                const IconComponent = ICON_MAP[service.icon] || Building2;
                return (
                  <div 
                    key={service.card_id} 
                    className={`relative p-8 bg-gradient-to-br ${colors.bg} rounded-2xl ${colors.border} border overflow-hidden group hover:border-opacity-60 transition-all`}
                  >
                    {service.is_coming_soon && (
                      <div className="absolute top-4 right-4">
                        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                          <Clock className="w-3 h-3 mr-1" />
                          {lang === "en" ? "Coming Soon" : "Kommer Snart"}
                        </Badge>
                      </div>
                    )}
                    <div className={`w-14 h-14 ${colors.iconBg} rounded-xl flex items-center justify-center mb-6`}>
                      <IconComponent className={`w-7 h-7 ${colors.text}`} />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3 font-['Unbounded']">
                      {service[`title_${lang}`] || service.title}
                    </h3>
                    <p className="text-[#94A3B8] mb-6 text-sm">
                      {service[`description_${lang}`] || service.description}
                    </p>
                    {service.link && !service.is_coming_soon ? (
                      <Link to={service.link}>
                        <Button className={`w-full ${colors.text === "text-[#23F7DD]" ? "bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]" : `bg-${service.color}-500 text-white hover:bg-${service.color}-600`} font-bold`}>
                          <ArrowRight className="w-4 h-4 mr-2" />
                          {lang === "en" ? "Learn More" : "Les Mer"}
                        </Button>
                      </Link>
                    ) : (
                      <div className="space-y-3">
                        <input
                          type="email"
                          placeholder={t.emailPlaceholder}
                          value={notifyType === service.card_id ? notifyEmail : ""}
                          onChange={(e) => { setNotifyEmail(e.target.value); setNotifyType(service.card_id); }}
                          className={`w-full px-4 py-2 bg-white/5 ${colors.border} border rounded-lg text-white placeholder:text-[#64748B] focus:outline-none`}
                        />
                        <Button 
                          onClick={() => handleNotifySubmit(service.card_id)}
                          className={`w-full ${colors.text === "text-[#23F7DD]" ? "bg-[#23F7DD] text-[#050505]" : `bg-${service.color}-500 text-white`} font-bold`}
                          disabled={notifySuccess && notifyType === service.card_id}
                        >
                          {notifySuccess && notifyType === service.card_id ? (
                            <><CheckCircle2 className="w-4 h-4 mr-2" /> {t.thankYou}</>
                          ) : (
                            <><Bell className="w-4 h-4 mr-2" /> {t.notifyMe}</>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              /* Default hardcoded services if CMS is empty */
              <>
                {/* For Companies (B2B) - Cyan */}
                <div className="relative p-8 bg-gradient-to-br from-[#4F46E5]/20 to-[#4F46E5]/5 rounded-2xl border border-[#4F46E5]/30 overflow-hidden group hover:border-[#4F46E5]/60 transition-all">
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-[#4F46E5]/20 text-[#4F46E5] border-[#4F46E5]/30">
                      <Building2 className="w-3 h-3 mr-1" />
                      {lang === "en" ? "For Businesses" : "For Bedrifter"}
                    </Badge>
                  </div>
                  <div className="w-14 h-14 bg-[#4F46E5]/20 rounded-xl flex items-center justify-center mb-6">
                    <Building2 className="w-7 h-7 text-[#4F46E5]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3 font-['Unbounded']">
                    {lang === "en" ? "Scaffolding for Businesses" : "Stillas for Bedrifter"}
                  </h3>
                  <p className="text-[#94A3B8] mb-6 text-sm">
                    {lang === "en" ? "Professional scaffolding solutions for contractors, developers and construction companies in Oslo and Viken." : "Profesjonelle stillasløsninger for entreprenører, utviklere og byggefirmaer i Oslo og Viken."}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {(lang === "en" ? ["Complete project delivery", "HAKI scaffolding systems", "HSE certified teams"] : ["Komplett prosjektlevering", "HAKI-stillassystemer", "HMS-sertifiserte team"]).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#94A3B8]">
                        <CheckCircle2 className="w-4 h-4 text-[#4F46E5] mt-0.5 shrink-0" />{f}
                      </li>
                    ))}
                  </ul>
                  <div className="space-y-2">
                    <Link to="/stillasfirma-oslo">
                      <Button className="w-full bg-[#4F46E5] text-white hover:bg-[#5B52F0] font-bold">
                        <ArrowRight className="w-4 h-4 mr-2" />
                        {lang === "en" ? "Get Quote" : "Be om Tilbud"}
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* For Private Homeowners - Orange */}
                <div className="relative p-8 bg-gradient-to-br from-[#F7931A]/20 to-[#F7931A]/5 rounded-2xl border border-[#F7931A]/30 overflow-hidden group hover:border-[#F7931A]/60 transition-all">
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-[#F7931A]/20 text-[#F7931A] border-[#F7931A]/30">
                      <Home className="w-3 h-3 mr-1" />
                      {lang === "en" ? "For Homeowners" : "For Private"}
                    </Badge>
                  </div>
                  <div className="w-14 h-14 bg-[#F7931A]/20 rounded-xl flex items-center justify-center mb-6">
                    <Home className="w-7 h-7 text-[#F7931A]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3 font-['Unbounded']">
                    {lang === "en" ? "Home Scaffolding" : "Stillas til Bolig"}
                  </h3>
                  <p className="text-[#94A3B8] mb-6 text-sm">
                    {lang === "en" ? "Safe and flexible scaffolding solutions for your house, facade or renovation project in Oslo and Viken." : "Trygge og fleksible stillasløsninger til ditt hus, fasade eller rehabiliteringsprosjekt i Oslo og Viken."}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {(lang === "en" ? ["Facade & roof work", "Fast delivery", "Professional assembly"] : ["Fasade- og takarbeid", "Rask levering", "Profesjonell montering"]).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#94A3B8]">
                        <CheckCircle2 className="w-4 h-4 text-[#F7931A] mt-0.5 shrink-0" />{f}
                      </li>
                    ))}
                  </ul>
                  <div className="space-y-2">
                    <Link to="/stillas-privat-oslo">
                      <Button className="w-full bg-[#F7931A] text-white hover:bg-[#FF9F2E] font-bold">
                        <ArrowRight className="w-4 h-4 mr-2" />
                        {lang === "en" ? "Get Quote" : "Få Tilbud"}
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* For Workers - Green */}
                <div className="relative p-8 bg-gradient-to-br from-[#23F7DD]/20 to-[#23F7DD]/5 rounded-2xl border border-[#23F7DD]/30 overflow-hidden group hover:border-[#23F7DD]/60 transition-all">
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-[#23F7DD]/20 text-[#23F7DD] border-[#23F7DD]/30">
                      <Sparkles className="w-3 h-3 mr-1" />
                      {lang === "en" ? "Now Hiring" : "Vi Ansetter"}
                    </Badge>
                  </div>
                  <div className="w-14 h-14 bg-[#23F7DD]/20 rounded-xl flex items-center justify-center mb-6">
                    <HardHat className="w-7 h-7 text-[#23F7DD]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3 font-['Unbounded']">
                    {lang === "en" ? "Scaffolding Jobs" : "Stillasjobb"}
                  </h3>
                  <p className="text-[#94A3B8] mb-6 text-sm">
                    {lang === "en" ? "Join AllStillas as a scaffolder. Stable work, competitive conditions, and professional growth in Oslo and Viken." : "Bli en del av AllStillas som stillasmontør. Stabilt arbeid, konkurransedyktige betingelser og profesjonell vekst i Oslo og Viken."}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {(lang === "en" ? ["Permanent employment", "Competitive conditions", "Individuals & teams welcome"] : ["Fast ansettelse", "Konkurransedyktige betingelser", "Enkeltpersoner og lag velkomne"]).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#94A3B8]">
                        <CheckCircle2 className="w-4 h-4 text-[#23F7DD] mt-0.5 shrink-0" />{f}
                      </li>
                    ))}
                  </ul>
                  <div className="space-y-2">
                    <Link to="/stillasjobb-oslo">
                      <Button className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold">
                        <UserPlus className="w-4 h-4 mr-2" />
                        {lang === "en" ? "Apply Today" : "Søk i Dag"}
                      </Button>
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* PROJECT GALLERY - Social Proof */}
          {projectImages.length > 0 && (
            <div className="mt-16 overflow-hidden">
              <div className="text-center mb-8">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Camera className="w-6 h-6 text-[#F7931A]" />
                  <h3 className="text-xl md:text-2xl font-['Unbounded'] font-bold text-white">
                    {lang === "no" ? "Våre Prosjekter" : "Our Projects"}
                  </h3>
                </div>
                <p className="text-[#94A3B8] text-sm">
                  {lang === "no" 
                    ? "Se noen av våre gjennomførte stillasprosjekter i Oslo og Viken"
                    : "See some of our completed scaffolding projects in Oslo and Viken"}
                </p>
              </div>
              <InfiniteImageCarousel 
                images={projectImages}
                speed={30}
                pauseOnHover={true}
                className="py-2"
              />
            </div>
          )}

          {/* HAKI Badge and Main Hero Content */}
          <div className="text-center mt-16">
            <Badge className="bg-[#23F7DD]/10 text-[#23F7DD] border-[#23F7DD]/30 px-4 py-2 text-sm mb-6">
              🏗️ HAKI • LAYHER | {lang === "en" ? "Heavy & Light Scaffold • Roofs • Complete Service" : "Tungt & Lett Stillas • Tak • Komplett Service"}
            </Badge>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-['Unbounded'] font-black text-white leading-tight mb-6">
              {getHeroTitle()}
            </h1>
            <p className="text-lg md:text-xl text-[#94A3B8] max-w-2xl mx-auto mb-8">
              {getHeroSubtitle()}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center gap-4 mb-6">
              <Link to="/hvordan-det-fungerer">
                <Button size="lg" className="bg-orange-500 text-white hover:bg-orange-400 font-bold text-lg px-8 py-6 gap-2 shadow-lg shadow-orange-500/30">
                  <Play className="w-5 h-5" />
                  {lang === "no" ? "Se hvordan det fungerer" : "See how it works"}
                </Button>
              </Link>
            </div>

            {/* Login link for returning users */}
            {!user && (
              <div className="mb-10 text-center">
                <Link to="/login" className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-[#23F7DD] transition-colors">
                  <span>{lang === 'no' ? 'Allerede bruker?' : 'Already a user?'}</span>
                  <span className="font-semibold text-[#23F7DD] underline underline-offset-4">{t.logIn}</span>
                  <ArrowRight className="w-4 h-4 text-[#23F7DD]" />
                </Link>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 max-w-4xl mx-auto mt-8">
              {getCmsStats().length > 0 ? (
                getCmsStats().map((stat, i) => {
                  const IconComponent = ICON_MAP[stat.icon] || TrendingUp;
                  return (
                    <div key={stat.stat_id || i} className="p-4 md:p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 text-center">
                      <IconComponent className="w-6 h-6 text-[#23F7DD] mb-2 mx-auto" />
                      <div className="text-2xl md:text-4xl font-bold text-white font-['Unbounded']">{stat.value}</div>
                      <div className="text-xs md:text-sm text-[#94A3B8]">{stat[`label_${lang}`] || stat.label}</div>
                    </div>
                  );
                })
              ) : (
                [
                  { value: stats.projects, label: t.stats.projects, icon: Briefcase, color: "text-purple-400", isComingSoon: false },
                  { value: stats.workers, label: t.stats.workers, icon: HardHat, color: "text-[#23F7DD]", isComingSoon: false },
                  { value: stats.products, label: t.stats.products, icon: Package, color: "text-[#F7931A]", isComingSoon: stats.products === 0 },
                  { value: stats.companies, label: t.stats.companies, icon: Building2, color: "text-blue-400", isComingSoon: false }
                ].map((stat, i) => (
                  <div key={i} className="p-4 md:p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 text-center">
                    <stat.icon className={`w-6 h-6 ${stat.color} mb-2 mx-auto`} />
                    {stat.isComingSoon ? (
                      <div className="text-lg md:text-xl font-bold text-[#F7931A] font-['Unbounded']">
                        {lang === "en" ? "Coming Soon" : "Kommer Snart"}
                      </div>
                    ) : (
                      <div className="text-2xl md:text-4xl font-bold text-white font-['Unbounded']">{stat.value}+</div>
                    )}
                    <div className="text-xs md:text-sm text-[#94A3B8]">{stat.label}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Courses Coming Soon Section */}
      <section className="relative z-10 px-4 md:px-12 py-16 bg-gradient-to-b from-[#0A0A0A] to-[#050505]">
        <div className="max-w-6xl mx-auto">
          {/* Coming Soon - Courses Card */}
          <div className="mt-8">
            <div className="relative p-8 rounded-2xl border border-[#10b981]/30 bg-gradient-to-br from-[#10b981]/20 to-[#10b981]/5 overflow-hidden">
              <div className="absolute top-4 right-4">
                <Badge className="bg-[#10b981]/30 text-[#10b981] border-[#10b981]/50 px-3 py-1 text-xs font-bold animate-pulse">
                  {lang === "en" ? "COMING SOON" : "KOMMER SNART"}
                </Badge>
              </div>
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-20 h-20 rounded-2xl bg-[#10b981]/20 flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="w-10 h-10 text-[#10b981]" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-2xl font-bold text-white mb-2 font-['Unbounded']">
                    {lang === "en" ? "Scaffolding Courses & Certification" : "Stillaskurs og Sertifisering"}
                  </h3>
                  <p className="text-[#94A3B8] mb-4">
                    {lang === "en" 
                      ? "Professional training courses for scaffolders with online lessons, video tutorials, and certification exams. Get certified and advance your career!"
                      : "Profesjonelle opplæringskurs for stillasmontører med online leksjoner, videoopplæring og sertifiseringseksamener. Bli sertifisert og avansér karrieren din!"}
                  </p>
                  <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                    <span className="px-3 py-1 bg-[#10b981]/10 border border-[#10b981]/30 rounded-full text-[#10b981] text-sm">
                      📚 {lang === "en" ? "Online Lessons" : "Online Leksjoner"}
                    </span>
                    <span className="px-3 py-1 bg-[#10b981]/10 border border-[#10b981]/30 rounded-full text-[#10b981] text-sm">
                      🎬 {lang === "en" ? "Video Tutorials" : "Video Opplæring"}
                    </span>
                    <span className="px-3 py-1 bg-[#10b981]/10 border border-[#10b981]/30 rounded-full text-[#10b981] text-sm">
                      📝 {lang === "en" ? "Quiz & Exams" : "Quiz og Eksamen"}
                    </span>
                    <span className="px-3 py-1 bg-[#10b981]/10 border border-[#10b981]/30 rounded-full text-[#10b981] text-sm">
                      🏆 {lang === "en" ? "Certificate" : "Sertifikat"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Videos from CMS */}
      {getCmsVideos().length > 0 && (
        <section className="relative z-10 px-4 md:px-12 py-16 bg-[#050505]">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {getCmsVideos().map((video) => {
                const colors = COLOR_MAP[video.color] || COLOR_MAP.purple;
                const IconComponent = ICON_MAP[video.icon] || Building2;
                return (
                  <div key={video.video_id} className={`relative p-6 bg-gradient-to-br ${colors.bg} rounded-2xl ${colors.border} border overflow-hidden`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 ${colors.iconBg} rounded-lg flex items-center justify-center`}>
                        <IconComponent className={`w-5 h-5 ${colors.text}`} />
                      </div>
                      <div>
                        <h4 className="text-white font-bold">{video[`title_${lang}`] || video.title}</h4>
                        <p className="text-[#94A3B8] text-xs">{video[`subtitle_${lang}`] || video.subtitle}</p>
                      </div>
                    </div>
                    <div className="relative rounded-xl overflow-hidden bg-black/50 aspect-video">
                      <video
                        controls
                        className="w-full h-full object-cover"
                        poster={video.poster_url || "https://images.pexels.com/photos/2219024/pexels-photo-2219024.jpeg?auto=compress&cs=tinysrgb&w=800"}
                      >
                        <source src={video.video_url} type="video/mp4" />
                      </video>
                    </div>
                    {video.button_link && (
                      <Link to={video.button_link} className="block mt-4">
                        <Button className={`w-full ${colors.text === "text-[#23F7DD]" ? "bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]" : `bg-${video.color}-500 text-white hover:bg-${video.color}-600`} font-bold`}>
                          <Play className="w-4 h-4 mr-2" />
                          {video[`button_text_${lang}`] || video.button_text || (lang === "en" ? "Learn More" : "Les Mer")}
                        </Button>
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials from CMS */}
      {getCmsTestimonials().length > 0 && (
        <section className="relative z-10 px-4 md:px-12 py-20 bg-[#0A0A0A]">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-['Unbounded'] font-bold text-white mb-4">
                {lang === "en" ? "What Our Clients Say" : "Hva Kundene Sier"}
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {getCmsTestimonials().map((testimonial) => (
                <div key={testimonial.testimonial_id} className="p-6 bg-white/5 rounded-2xl border border-white/10">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating || 5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-[#94A3B8] italic mb-6">"{testimonial[`quote_${lang}`] || testimonial.quote}"</p>
                  <div className="flex items-center gap-3">
                    {testimonial.author_image && (
                      <img src={testimonial.author_image} alt={testimonial.author_name} className="w-12 h-12 rounded-full object-cover" />
                    )}
                    <div>
                      <p className="text-white font-semibold">{testimonial.author_name}</p>
                      <p className="text-[#64748B] text-sm">
                        {testimonial[`author_role_${lang}`] || testimonial.author_role}
                        {testimonial.author_company && `, ${testimonial.author_company}`}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="relative z-10 px-4 md:px-12 py-20 bg-[#0A0A0A]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-['Unbounded'] font-bold text-white mb-4">{t.cta}</h2>
          <p className="text-[#94A3B8] mb-8">{t.ctaDesc}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold px-8">{t.getStarted}</Button>
            </Link>
            <Link to="/webshop">
              <Button size="lg" variant="outline" className="border-[#F7931A]/50 text-[#F7931A] hover:bg-[#F7931A]/10 font-bold px-8">
                <ShoppingBag className="w-4 h-4 mr-2" />{t.shop}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Strip - Turquoise Banner */}
      <section className="relative z-10 px-4 md:px-12 py-12 bg-[#23F7DD]">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Shield, text: lang === "en" ? "Full Compliance" : "Full Compliance" },
            { icon: Truck, text: lang === "en" ? "Fast Delivery" : "Rask Levering" },
            { icon: Star, text: lang === "en" ? "Quality Assured" : "Kvalitetssikret" },
            { icon: TrendingUp, text: lang === "en" ? "Trusted Contractor" : "Pålitelig Entreprenør" }
          ].map((item, i) => (
            <div key={i} className="flex items-center justify-center gap-3 text-[#050505]">
              <item.icon className="w-6 h-6" />
              <span className="font-bold text-sm md:text-base">{item.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Footer - from CMS */}
      <footer className="relative z-10 px-4 md:px-12 py-12 bg-[#050505] border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <Link to="/" className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-[#23F7DD] rounded-lg flex items-center justify-center">
                  <span className="text-[#050505] font-bold text-lg font-['Unbounded']">All</span>
                </div>
                <span className="text-xl font-bold text-white font-['Unbounded']">Stillas</span>
              </Link>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-[#23F7DD]/10 to-transparent border border-[#23F7DD]/20 rounded-full">
                <span className="text-[#23F7DD] text-xs">⚡</span>
                <span className="text-xs text-[#94A3B8]">Powered by</span>
                <span className="text-xs text-[#23F7DD] font-medium">Doserne AS</span>
              </div>
              {getCmsFooter().org_number && (
                <p className="text-[#64748B] text-xs mt-2">Org.nr: {getCmsFooter().org_number}</p>
              )}
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{lang === "en" ? "Services" : "Tjenester"}</h4>
              <ul className="space-y-2 text-[#64748B] text-sm">
                <li><Link to="/webshop" className="hover:text-[#23F7DD]">{t.shop}</Link></li>
                <li><Link to="/rentals" className="hover:text-[#23F7DD]">{t.rent}</Link></li>
                <li><Link to="/projects" className="hover:text-[#23F7DD]">{t.projects}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{lang === "en" ? "Company" : "Selskap"}</h4>
              <ul className="space-y-2 text-[#64748B] text-sm">
                <li><Link to="/about" className="hover:text-[#23F7DD]">{lang === "en" ? "About Us" : "Om Oss"}</Link></li>
                <li><Link to="/register?type=worker" className="hover:text-[#23F7DD]">{t.workers}</Link></li>
                <li><Link to="/contact" className="hover:text-[#23F7DD]">{lang === "en" ? "Contact" : "Kontakt"}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{lang === "en" ? "Contact" : "Kontakt"}</h4>
              <ul className="space-y-2 text-[#64748B] text-sm">
                {getCmsFooter().email && (
                  <li className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />{getCmsFooter().email}
                  </li>
                )}
                {getCmsFooter().phone && (
                  <li className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />{getCmsFooter().phone}
                  </li>
                )}
                {getCmsFooter().address && (
                  <li className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />{getCmsFooter().address}
                  </li>
                )}
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#64748B] text-sm">
              © 2026 Doserne AS. All rights reserved.
            </p>
            {getCmsFooter().social_links && (
              <div className="flex gap-4">
                {getCmsFooter().social_links.facebook && (
                  <a href={getCmsFooter().social_links.facebook} target="_blank" rel="noopener noreferrer" className="text-[#64748B] hover:text-[#23F7DD]">Facebook</a>
                )}
                {getCmsFooter().social_links.instagram && (
                  <a href={getCmsFooter().social_links.instagram} target="_blank" rel="noopener noreferrer" className="text-[#64748B] hover:text-[#23F7DD]">Instagram</a>
                )}
                {getCmsFooter().social_links.linkedin && (
                  <a href={getCmsFooter().social_links.linkedin} target="_blank" rel="noopener noreferrer" className="text-[#64748B] hover:text-[#23F7DD]">LinkedIn</a>
                )}
              </div>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
